/**
 * Оркестратор проверок в браузере.
 *
 * Последовательность важна:
 *   1. создаём сессию и получаем план (адреса probe, список блокировок, STUN);
 *   2. probe-запросы — они создают наблюдения на серверах, без них отчёта нет;
 *   3. остальные проверки параллельно: WebRTC, блокировки, окружение, RTT;
 *   4. отправляем собранное на сервер и забираем готовый отчёт.
 *
 * Каждый шаг сообщает о себе через onProgress, чтобы UI показывал, что
 * происходит: часть проверок занимает секунды (загрузка заблокированных URL по
 * таймауту), и молчащий индикатор выглядел бы как зависание.
 *
 * Ни одна проверка не бросает исключение наружу: провал отдельной проверки —
 * это данные для отчёта, а не сбой. Падать целиком имеет смысл только если
 * недоступен сам API.
 */

import type { ClientResults, SessionResponse, Report } from '@vpncheck/shared';
import { createSession, fetchReport, submitClientResults } from '../api.js';
import { runProbes } from './probes.js';
import { collectIceCandidates } from './webrtc.js';
import { runBlockedChecks } from './blocked.js';
import { collectEnv } from './env.js';
import { measureWsLatency } from './latency.js';

/** Шаги проверки в порядке выполнения. Идентификаторы используются в UI. */
export type StepId =
  | 'session'
  | 'probes'
  | 'webrtc'
  | 'blocked'
  | 'env'
  | 'latency'
  | 'report';

export type StepStatus = 'pending' | 'running' | 'done' | 'failed';

export interface StepState {
  id: StepId;
  title: string;
  status: StepStatus;
  /** Короткая строка результата для показа рядом с шагом. */
  note?: string;
}

export const STEP_TITLES: Record<StepId, string> = {
  session: 'Подготовка сессии',
  probes: 'Запросы к точкам в РФ и за рубежом',
  webrtc: 'Сбор ICE-кандидатов (WebRTC)',
  blocked: 'Доступность заблокированных ресурсов',
  env: 'Окружение браузера',
  latency: 'Замер задержек',
  report: 'Сборка отчёта',
};

export interface RunOptions {
  /** Согласие на скан портов выходного IP. */
  portScanConsent: boolean;
  onProgress: (step: StepId, status: StepStatus, note?: string) => void;
}

export interface RunOutcome {
  token: string;
  report: Report;
}

export async function runAllChecks(options: RunOptions): Promise<RunOutcome> {
  const { onProgress } = options;

  onProgress('session', 'running');
  let session: SessionResponse;
  try {
    session = await createSession();
  } catch (err) {
    onProgress('session', 'failed', message(err));
    throw err;
  }
  const { token, plan } = session;
  onProgress('session', 'done');

  // Probe обязателен: без наблюдения российской точки сервер не соберёт отчёт.
  onProgress('probes', 'running');
  const probes = await runProbes(plan.ruProbeUrl, plan.intlProbeUrl);
  if (!probes.ru.response) {
    onProgress('probes', 'failed', probes.ru.error ?? 'точка в РФ недоступна');
    throw new Error(
      `Не удалось выполнить запрос к точке в РФ: ${probes.ru.error ?? 'неизвестная ошибка'}`,
    );
  }
  onProgress(
    'probes',
    'done',
    probes.intl.response
      ? `РФ: ${probes.ru.response.ip} · зарубеж: ${probes.intl.response.ip}`
      : `РФ: ${probes.ru.response.ip} · зарубежная точка не ответила`,
  );

  // Время сервера берём из наблюдения probe: по нему считается расхождение часов.
  const serverTimeMs = Date.parse(probes.ru.response.observedAt);

  // Остальные проверки независимы друг от друга — выполняем параллельно.
  const [webrtc, blocked, env, wsRtt] = await Promise.all([
    step(
      'webrtc',
      () => collectIceCandidates(plan.stunUrls),
      { supported: false, candidates: [], error: 'проверка не выполнена' },
      onProgress,
      (r) => (r.supported ? `кандидатов: ${r.candidates.length}` : 'WebRTC недоступен'),
    ),
    step(
      'blocked',
      () => runBlockedChecks(plan.blockedTargets, plan.blockedTimeoutMs),
      [],
      onProgress,
      (r) => `открылось ${r.filter((x) => x.reachable).length} из ${r.length}`,
    ),
    step(
      'env',
      async () => collectEnv(Number.isFinite(serverTimeMs) ? serverTimeMs : null),
      collectEnv(null),
      onProgress,
      (r) => r.timezone ?? 'таймзона неизвестна',
    ),
    step(
      'latency',
      () => measureWsLatency(plan.wsUrl),
      { rttMs: null, samples: 0 },
      onProgress,
      (r) => (r.rttMs === null ? 'замер не удался' : `${Math.round(r.rttMs)} мс`),
    ),
  ]);

  const results: ClientResults = {
    webrtc,
    blocked,
    env,
    latency: {
      wsRttMs: wsRtt.rttMs,
      samples: wsRtt.samples,
      ruHttpRttMs: probes.ru.rttMs,
      intlHttpRttMs: probes.intl.rttMs,
    },
    portScanConsent: options.portScanConsent,
  };

  onProgress('report', 'running');
  try {
    await submitClientResults(token, results);
    const { report } = await fetchReport(token);
    onProgress('report', 'done');
    return { token, report };
  } catch (err) {
    onProgress('report', 'failed', message(err));
    throw err;
  }
}

/**
 * Выполняет шаг, сообщая о статусе.
 *
 * Ошибку не пробрасывает: возвращает `fallback`, чтобы отчёт собрался и без этой
 * проверки. Непройденная проверка — это тоже результат, и сервер отметит её в
 * списке невыполненных вместо того, чтобы вернуть пустой отчёт.
 */
async function step<T>(
  id: StepId,
  run: () => Promise<T>,
  fallback: T,
  onProgress: RunOptions['onProgress'],
  describe: (result: T) => string,
): Promise<T> {
  onProgress(id, 'running');
  try {
    const result = await run();
    onProgress(id, 'done', describe(result));
    return result;
  } catch (err) {
    onProgress(id, 'failed', message(err));
    return fallback;
  }
}

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
